/**
 * useTeamAnalysis — fetches live team analysis data for all 10 staffing components.
 *
 * Calls POST /api/staffing/team-analysis-data and returns pre-shaped component data.
 * Automatically re-fetches when opportunity or team composition changes.
 */
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../../utils/api';

export function useTeamAnalysis(opportunityId, staffingRoles, overrideData = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable ref for team member fingerprint
  const lastFingerprintRef = useRef(null);

  // Build team members list from staffing roles
  const teamMembers = (staffingRoles || [])
    .filter(r => r.status === 'assigned' && r.assigned_employee_id)
    .map((r, i) => ({
      employeeId: r.assigned_employee_id,
      role: r.role_name,
      roleIndex: i,
      roleId: r.id,
    }));

  const fingerprint = opportunityId && teamMembers.length
    ? `${opportunityId}::${teamMembers.map(m => `${m.employeeId}/${m.role}`).join('|')}`
    : null;

  // When a saved config's chemistry data is injected, seed it directly and arm
  // the fingerprint so the normal fetch effect sees no change and stays idle.
  useEffect(() => {
    if (overrideData) {
      setData(overrideData);
      lastFingerprintRef.current = fingerprint; // suppress upcoming fetch
    }
  }, [overrideData]); // fingerprint omitted: React 18 batches setInjectedChemistryData+setStaffingData into one render, so fingerprint is current when this fires

  useEffect(() => {
    if (!fingerprint) return;
    if (fingerprint === lastFingerprintRef.current) return;

    let cancelled = false;
    lastFingerprintRef.current = fingerprint;

    const fetchData = async () => {
      setData(null);   // Clear stale data immediately so UI shows nothing while loading
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('staffing/team-analysis-data', {
          method: 'POST',
          body: JSON.stringify({ opportunityId, teamMembers }),
        });

        if (cancelled) return;

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || `HTTP ${response.status}`);
        }

        const json = await response.json();
        if (json.status === 'success') {
          setData(json.data);
        } else {
          throw new Error(json.message || 'Analysis failed');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          console.error('useTeamAnalysis error:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
